import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { firestore } from '../../helpers/Firebase';

/**
 * Returns true if a provider's `featured` field represents an active
 * featured window (object with endDate in the future). Mirrors the
 * mobile app's getFeaturedIndex check — an SP with an expired endDate
 * is treated as not currently featured, even though the Firestore
 * object is still truthy. De-featured providers (field === false)
 * also return false.
 */
export const isFeaturedActive = (featured) => {
  if (!featured || typeof featured !== 'object') return false;
  if (!featured.endDate) return false;
  return moment().isBefore(moment(featured.endDate));
};

/**
 * Hook to fetch featured SP daily analytics (GA4-sourced) for a date range
 * including the previous period. Mirrors useDailyAnalytics but reads from
 * the `featuredSpDaily` collection written by generateFeaturedSpDaily /
 * backfillFeaturedSpDaily in functions/index.js.
 *
 * Doc shape: featuredSpDaily/{YYYY-MM-DD}
 *   { date, timestamp, SP: { [spId]: { impressions, clicks, orders, revenue, ctr, conversionRate } },
 *     general: { totalImpressions, totalClicks, totalOrders, totalRevenue, uniqueSPs, avgCtr, avgConversionRate, lastUpdate },
 *     createdAt, backfilled?, dataQuality? }
 *
 * @param {object} dateRange - { startTimestamp, endTimestamp }
 * @param {string} spId - Optional SP key to extract per-day spData
 */
export const useFeaturedSpDaily = (dateRange, spId = null) => {
  const [data, setData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const prevValuesRef = useRef({ startTimestamp: null, endTimestamp: null, spId: null });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!dateRange || !dateRange.startTimestamp || !dateRange.endTimestamp) {
          setLoading(false);
          return;
        }

        const { startTimestamp, endTimestamp } = dateRange;

        const isInitialLoad = prevValuesRef.current.startTimestamp === null;
        const valuesChanged =
          prevValuesRef.current.startTimestamp !== startTimestamp ||
          prevValuesRef.current.endTimestamp !== endTimestamp ||
          prevValuesRef.current.spId !== spId;

        if (!isInitialLoad && !valuesChanged) {
          return;
        }

        prevValuesRef.current = { startTimestamp, endTimestamp, spId };

        setLoading(true);

        const periodLength = endTimestamp - startTimestamp;
        const previousStartTimestamp = startTimestamp - periodLength;
        const previousEndTimestamp = startTimestamp;

        const [currentSnapshot, previousSnapshot] = await Promise.all([
          firestore()
            .collection('featuredSpDaily')
            .where('timestamp', '>=', startTimestamp)
            .where('timestamp', '<=', endTimestamp)
            .orderBy('timestamp', 'asc')
            .get(),
          firestore()
            .collection('featuredSpDaily')
            .where('timestamp', '>=', previousStartTimestamp)
            .where('timestamp', '<', previousEndTimestamp)
            .orderBy('timestamp', 'asc')
            .get(),
        ]);

        const mapDoc = (doc) => {
          const docData = doc.data();
          const dayData = {
            date: docData.date || moment(docData.timestamp).format('MMM DD'),
            timestamp: docData.timestamp,
            general: docData.general || {},
            SP: docData.SP || {},
            dataQuality: docData.dataQuality || null,
          };
          if (spId && docData.SP && docData.SP[spId]) {
            dayData.spData = docData.SP[spId];
          }
          return dayData;
        };

        setData(currentSnapshot.docs.map(mapDoc));
        setPreviousData(previousSnapshot.docs.map(mapDoc));
        setError(null);
      } catch (err) {
        console.error('Error fetching featured SP daily:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.startTimestamp, dateRange?.endTimestamp, spId]);

  return { data, previousData, loading, error };
};

/**
 * Aggregate a list of featuredSpDaily day records into totals.
 * Handles three views:
 *   - Admin (no spId): sum general totals across days.
 *   - SP user (spId provided): sum SP[spId] across days.
 *   - Single provider drilldown: same as SP user.
 *
 * CTR and conversion rate are computed from the aggregated totals
 * (clicks/impressions, orders/clicks) rather than averaging per-day rates,
 * so heavy days dominate correctly.
 */
export const aggregateFeaturedTotals = (days, spId = null) => {
  let impressions = 0;
  let clicks = 0;
  let orders = 0;
  let revenue = 0;

  (days || []).forEach((day) => {
    if (spId) {
      const s = day.SP?.[spId];
      if (!s) return;
      impressions += s.impressions || 0;
      clicks += s.clicks || 0;
      orders += s.orders || 0;
      revenue += s.revenue || 0;
    } else {
      const g = day.general || {};
      impressions += g.totalImpressions || 0;
      clicks += g.totalClicks || 0;
      orders += g.totalOrders || 0;
      revenue += g.totalRevenue || 0;
    }
  });

  return {
    impressions,
    clicks,
    orders,
    revenue,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    conversionRate: clicks > 0 ? (orders / clicks) * 100 : 0,
  };
};

/**
 * Returns true if any day in the given range has non-zero featured activity
 * for the specified SP (impressions, clicks, or revenue). Used to decide
 * whether to render the featured section for a specific provider — SPs that
 * were previously featured but have since been removed still surface their
 * historical data, while SPs who were never featured stay hidden.
 */
export const hasFeaturedActivity = (days, spId) => {
  if (!spId || !days) return false;
  return days.some((day) => {
    const s = day.SP?.[spId];
    return !!s && ((s.impressions || 0) > 0 || (s.clicks || 0) > 0 || (s.revenue || 0) > 0);
  });
};

// Placement label / order configuration shared across the dashboard.
// Keys mirror the af_from values emitted by the mobile app; 'unknown' is the
// fallback bucket used by the cloud function for missing/(not set) values.
export const PLACEMENT_LABELS = {
  service_provider_list: 'Provider List',
  banners: 'Banners',
  unknown: 'Unknown',
};

export const PLACEMENT_ORDER = ['service_provider_list', 'banners', 'unknown'];

export const formatPlacementLabel = (placement) =>
  PLACEMENT_LABELS[placement] || placement;

// Stable color per placement so the table cells, chart segments, and any
// future per-placement KPI cards stay visually consistent.
export const PLACEMENT_COLORS = {
  service_provider_list: 'hsl(262, 83%, 58%)',
  banners: 'hsl(330, 81%, 60%)',
  unknown: 'hsl(220, 9%, 60%)',
};

/**
 * Aggregate per-placement (af_from) impressions/clicks across days. Days
 * predating the af_from rollout will not have byPlacement and contribute
 * nothing here, so legacy ranges yield an empty object.
 *
 * Returns: { [placement]: { impressions, clicks, ctr } }
 *   - Admin (no spId): walks day.general.byPlacement.
 *   - SP user / drilldown: walks day.SP[spId].byPlacement.
 *
 * CTR is computed from the aggregated totals so heavy days dominate, matching
 * aggregateFeaturedTotals' approach.
 */
export const aggregateFeaturedByPlacement = (days, spId = null) => {
  const acc = {};

  (days || []).forEach((day) => {
    const source = spId
      ? day.SP?.[spId]?.byPlacement
      : day.general?.byPlacement;
    if (!source) return;
    for (const [placement, vals] of Object.entries(source)) {
      if (!acc[placement]) acc[placement] = { impressions: 0, clicks: 0 };
      acc[placement].impressions += vals.impressions || 0;
      acc[placement].clicks += vals.clicks || 0;
    }
  });

  for (const placement of Object.keys(acc)) {
    const p = acc[placement];
    p.ctr = p.impressions > 0 ? (p.clicks / p.impressions) * 100 : 0;
  }

  return acc;
};

/**
 * Sorted union of placements that appear in either current or previous range.
 * Known placements come first in PLACEMENT_ORDER order, then any newcomers
 * sorted alphabetically (forward-compatible with new af_from values added in
 * the mobile app).
 */
export const listPlacements = (currentByPlacement, previousByPlacement) => {
  const present = new Set([
    ...Object.keys(currentByPlacement || {}),
    ...Object.keys(previousByPlacement || {}),
  ]);
  const known = PLACEMENT_ORDER.filter((p) => present.has(p));
  const extras = [...present].filter((p) => !PLACEMENT_ORDER.includes(p)).sort();
  return [...known, ...extras];
};

export default useFeaturedSpDaily;
